import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';
import { documentKeys } from '@/hooks/use-documents';

import type { Query, QueryClient } from '@tanstack/react-query';

/** The four kinds of record a Pulse-written document can cite. */
export const DOCUMENT_SOURCE_KINDS = [
  'email',
  'knowledge_document',
  'task',
  'constituent',
] as const;

export type DocumentSourceKind = (typeof DOCUMENT_SOURCE_KINDS)[number];

/** One cited record, reduced to what the Sources section renders and opens. */
export interface DocumentSource {
  readonly id: string;
  readonly kind: DocumentSourceKind;
  /** The record's own id: the email, task, constituent or Knowledge document. */
  readonly sourceId: string;
  /** The thread an email source was read from. Null for every other kind. */
  readonly correspondenceId: string | null;
  /** The label stored at write time: a subject, title or name. */
  readonly label: string;
  readonly sectionId: string;
  readonly ordinal: number;
}

/**
 * Explicit columns, never `select('*')`: `document_sources` is on the
 * sensitive-relation lint map, because every label is a subject, a title or a
 * constituent's name.
 */
export const DOCUMENT_SOURCE_COLUMNS =
  'id, source_kind, source_id, correspondence_id, label, section_id, ordinal';

interface DocumentSourceRow {
  readonly id: string;
  readonly source_kind: string;
  readonly source_id: string;
  readonly correspondence_id: string | null;
  readonly label: string;
  readonly section_id: string;
  readonly ordinal: number;
}

function isSourceKind(value: string): value is DocumentSourceKind {
  return (DOCUMENT_SOURCE_KINDS as readonly string[]).includes(value);
}

/**
 * Rows as the section shows them. A kind this client does not know is left
 * out rather than rendered as something it cannot open.
 */
export function toDocumentSources(rows: readonly DocumentSourceRow[]): DocumentSource[] {
  const sources: DocumentSource[] = [];
  for (const row of rows) {
    if (!isSourceKind(row.source_kind)) {
      continue;
    }
    sources.push({
      id: row.id,
      kind: row.source_kind,
      sourceId: row.source_id,
      correspondenceId: row.correspondence_id,
      label: row.label,
      sectionId: row.section_id,
      ordinal: row.ordinal,
    });
  }
  return sources;
}

/**
 * The records a document was written from, in section then ordinal order.
 *
 * The SELECT policy admits a row only when the viewer can read both the
 * document and the record it names, so a row RLS hides is simply absent: there
 * is nothing to redact on this side. Viewer-scoped in the key for the same
 * reason, and under the `documents` root, which is never persisted.
 */
export function useDocumentSources(orgId: string | undefined, documentId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: documentKeys.sources(orgId, user?.id, documentId),
    enabled: Boolean(orgId && user?.id && documentId),
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }): Promise<DocumentSource[]> => {
      const { data, error } = await supabase
        .from('document_sources')
        .select(DOCUMENT_SOURCE_COLUMNS)
        .eq('document_id', documentId)
        .eq('organization_id', orgId)
        .order('section_id', { ascending: true })
        .order('ordinal', { ascending: true })
        .abortSignal(signal);
      if (error) {
        throw error;
      }
      return toDocumentSources((data ?? []) as DocumentSourceRow[]);
    },
  });
}

/** A cached source set (`documentKeys.sources`), whose labels RLS decided for one viewer. */
export function isDocumentSourcesKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'documents' && queryKey[3] === 'sources';
}

/** A cached source set or document origin: both are resolved under the viewer's RLS. */
export function isDocumentProvenanceKey(queryKey: readonly unknown[]): boolean {
  return (
    isDocumentSourcesKey(queryKey) || (queryKey[0] === 'documents' && queryKey[1] === 'origin')
  );
}

/** A record whose access the viewer just lost. */
export type RevokedSourceRecord =
  | { readonly kind: 'email'; readonly correspondenceId: string }
  | { readonly kind: 'task'; readonly taskId: string };

function citesRecord(source: DocumentSource, record: RevokedSourceRecord): boolean {
  return record.kind === 'email'
    ? source.kind === 'email' && source.correspondenceId === record.correspondenceId
    : source.kind === 'task' && source.sourceId === record.taskId;
}

/**
 * Drop every cached source set that could still show a record the viewer just lost. A set with no
 * data yet may be a read in flight that predates the loss, so it matches too. In-flight reads are
 * cancelled first, so a response authorised before the revoke never lands after it; entries off
 * screen are removed, and on-screen ones reset, so RLS decides their rows again.
 */
export async function resetRevokedDocumentSources(
  queryClient: QueryClient,
  record: RevokedSourceRecord,
): Promise<void> {
  const predicate = (query: Query): boolean => {
    if (!isDocumentSourcesKey(query.queryKey)) {
      return false;
    }
    const data: unknown = query.state.data;
    return (
      !Array.isArray(data) ||
      (data as readonly DocumentSource[]).some((source) => citesRecord(source, record))
    );
  };
  await queryClient.cancelQueries({ predicate });
  queryClient.removeQueries({
    predicate: (query) => predicate(query) && query.getObserversCount() === 0,
  });
  await queryClient.resetQueries({ predicate });
}
